import { useToasts } from "../../contexts/toasts";
import React, { useCallback, useEffect, useState } from "react";
import { StructureCoach } from "../../types/Structure";
import { Structures } from "../../api/Structure";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faInfoCircle,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import CoachItem from "../../Components/list-items/CoachItem";
import { TailSpin } from "react-loader-spinner";
import { Option } from "../../models/generics";
import { Form, Select } from "antd";
import "../Users/UsersList.css";

export default function CoachesList() {
  const { showToast } = useToasts();
  const [coaches, setCoaches] = useState<StructureCoach[]>();
  const [users, setUsers] = useState<Option[]>();
  const [error, setError] = useState(false);
  const [deleteCoachModal, setDeleteCoachModal] = useState(false);
  const [addCoachModal, setAddCoachModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coachToDelete, setCoachToDelete] = useState<StructureCoach>();
  const [coachForm] = Form.useForm();
  const selectedCoach = Form.useWatch("coach", coachForm);
  const [assignCoachLoading, setAssignCoachLoading] = useState(false);

  const initCoaches = useCallback(() => {
    Structures.getStructureCoaches().then(
      ({ data }) => {
        setCoaches(data.list);
      },
      () => {
        showToast("coaches-error", "Impossibile ottenere la lista dei maestri", "", "danger");
        setCoaches([]);
        setError(true);
      }
    );
  }, [showToast]);

  const initFavorites = useCallback(() => {
    Structures.getHostFavorites().then(
      ({ data }) => {
        setUsers(
          data.list.map((user) => ({
            label: `${user.user.name} ${user.user.familyName}`,
            value: user.userId,
          }))
        );
      },
      () => {
        showToast("coaches-error", "Impossibile ottenere la lista dei maestri", "", "danger");
        setUsers([]);
        setError(true);
      }
    );
  }, [showToast]);

  useEffect(() => {
    initCoaches();
    initFavorites();
  }, [initCoaches, initFavorites]);

  const reset = useCallback(() => {
    setError(false);
    setCoaches(undefined);
    initCoaches();
  }, [initCoaches]);

  const onDeleteCoach =
    (coach: StructureCoach) => (evt: React.MouseEvent<HTMLButtonElement>) => {
      evt.stopPropagation();
      setDeleteCoachModal(true);
      setCoachToDelete(coach);
    };

  const deleteCoach = () => {
    setLoading(true);
    Structures.dissociateCoach(coachToDelete!.userId).then(
      () => {
        showToast("coach-delete-success", "Maestro dissociato correttamente", "", "success");
        setLoading(false);
        reset();
        handleDeleteClose();
      },
      () => {
        setLoading(false);
        showToast("coach-delete-error", "Errore nella cancellazione del Maestro", "", "danger");
      }
    );
  };

  const handleDeleteClose = () => {
    setDeleteCoachModal(false);
    setCoachToDelete(undefined);
  };

  const handleAddClose = () => setAddCoachModal(false);

  const renderCoaches = () => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">C'è stato un errore, per favore riprova più tardi</span>
        </Alert>
      );

    if (coaches!.length === 0)
      return (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span className="ms-2">Nessun Maestro trovato</span>
        </Alert>
      );

    return (
      <Row className="g-3">
        {coaches!.map((coach, index) => (
          <Col xs={12} sm={6} md={4} lg={3} key={index}>
            <CoachItem coach={coach} onDelete={onDeleteCoach(coach)} />
          </Col>
        ))}
      </Row>
    );
  };

  const renderAsyncContent = () =>
    coaches ? (
      renderCoaches()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );

  const onAssignCoachSubmit = useCallback(
    (values: any) => {
      setAssignCoachLoading(true);
      const coachId = values.coach;
      if (coachId) {
        Structures.associateCoach(coachId).then(
          () => {
            showToast("coach-associate-success", "Maestro associato correttamente", "", "success");
            setAssignCoachLoading(false);
            reset();
            handleAddClose();
          },
          () => {
            setAssignCoachLoading(false);
            showToast("coach-associate-error", "Errore nell'assegnazione del Maestro", "", "danger");
          }
        );
      }
    },
    [reset, showToast]
  );

  return (
    <Container fluid="md" className="mt-5">

      {/* Toolbar */}
      <div className="users-toolbar">
        <div className="users-toolbar-top">
          <h4 className="users-title">
            I tuoi Maestri
            {coaches !== undefined && (
              <span className="users-count-badge">{coaches.length}</span>
            )}
          </h4>
          <button onClick={() => setAddCoachModal(true)} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Maestro</span>
          </button>
        </div>
      </div>

      {renderAsyncContent()}

      {/* Modal dissocia */}
      <Modal show={deleteCoachModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Dissocia Maestro</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Sei sicuro di voler rimuovere il maestro selezionato dalla tua lista?</p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">Questa azione non può essere annullata.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={deleteCoach}>
            {loading ? <TailSpin color="#FFF" width={20} height={20} /> : <span>Rimuovi</span>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal associa */}
      <Modal show={addCoachModal} onHide={handleAddClose}>
        <Modal.Header closeButton>
          <Modal.Title>Associa Maestro</Modal.Title>
        </Modal.Header>
        <Form form={coachForm} onFinish={onAssignCoachSubmit}>
          <Modal.Body>
            <p className="text-muted mb-3">
              Scegli un utente dalla lista da associare come maestro.
            </p>
            {!users ? (
              <Alert variant="info" className="py-2">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span className="ms-2">Nessun utente assegnabile</span>
              </Alert>
            ) : (
              <Form.Item label="Maestro" name="coach" required>
                <Select
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  className="w-100"
                  placeholder="Scegli un Maestro..."
                  options={users}
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </Form.Item>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" size="sm" className="border" onClick={handleAddClose}>
              Chiudi
            </Button>
            <Button type="submit" disabled={!selectedCoach} variant="success" size="sm">
              {assignCoachLoading
                ? <TailSpin color="#FFF" width={20} height={20} />
                : <span>Aggiungi</span>}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
}
